import Button from '@mui/material/Button';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import MenuItem from '@mui/material/MenuItem';
import Menu from '@mui/material/Menu';
import { SIM_PRODUCT_ACTION, SimProductAction } from './constants/simProductActions';
import { ActionConfirmDialog } from './ActionConfirmDialog';
import { useExternalRowSelectionCount } from 'src/common/components/ConfigurableTable/Components/Rows/ExternalRowSelection/hooks/useExternalRowSelectionCount';
import { useRSimMakeAction } from './hooks/useRSimMakeAction.ts';
import Stack from '@mui/material/Stack';
import Portal from '@mui/material/Portal';
import { AutoHideSnackbar } from 'src/common/Snackbar/AutoHideSnackbar';

import { AlertTitle } from 'common/Snackbar/AlertTitle';

import Typography from '@mui/material/Typography';
import { useEnabledRSimProductActions } from './hooks/useEnabledRSimProductActions.ts';
import { getActionTranslationName } from 'products/sim/Actions/utils/simProductActionTranslationsUtils';
import { Actions, Subjects } from 'permissions/ability.ts';
import { SimInventoryCan } from 'permissions/PermissionProvider.tsx';

export const RSimProductActionSelectButton = () => {
  const { t } = useTranslation();

  const [actionSucceeded, setActionSucceeded] = useState<boolean>(false);
  const [actionFailed, setActionFailed] = useState<boolean>(false);
  const actionNameRef = useRef('');

  const [action, setAction] = useState<SimProductAction | null>(null);

  const numberOfSelectedRows = useExternalRowSelectionCount();

  const { inLifeActions, inLifeActionsNotSupportedSummary, inLifeActionsSupportedSummary } =
    useEnabledRSimProductActions();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const { makeAction, isPending } = useRSimMakeAction({
    onSuccess: () => {
      actionNameRef.current = getActionTranslationName(action!, t);
      setActionSucceeded(true);
      setAnchorEl(null);
      setAction(null);
    },
    onError: () => {
      actionNameRef.current = getActionTranslationName(action!, t);
      setAnchorEl(null);
      setAction(null);
      setActionFailed(true);
    },
  });

  return (
    <>
      <Button
        aria-controls={open ? 'grouped-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
        color="secondary"
        endIcon={<ArrowDropDownIcon />}
        disabled={numberOfSelectedRows === 0 || inLifeActions.size === 0}
      >
        {t('products.sim.actions.action')}
      </Button>
      <Menu
        id="grouped-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{
          list: {
            'aria-labelledby': 'basic-button',
            sx: {
              py: 0,
            },
          },
        }}
      >
        <SimInventoryCan I={Actions.make} a={Subjects.inLifeActivation}>
          <MenuItem
            disabled={!inLifeActions.has(SIM_PRODUCT_ACTION.ACTIVATE)}
            onClick={() => {
              setAction(SIM_PRODUCT_ACTION.ACTIVATE);
            }}
          >
            {t('products.sim.actions.activate')}
          </MenuItem>
        </SimInventoryCan>

        <SimInventoryCan I={Actions.make} a={Subjects.inLifeSuspension}>
          <MenuItem
            disabled={!inLifeActions.has(SIM_PRODUCT_ACTION.SUSPEND)}
            onClick={() => {
              setAction(SIM_PRODUCT_ACTION.SUSPEND);
            }}
          >
            {t('products.sim.actions.suspend')}
          </MenuItem>
        </SimInventoryCan>

        <SimInventoryCan I={Actions.make} a={Subjects.inLifeUnsuspension}>
          <MenuItem
            disabled={!inLifeActions.has(SIM_PRODUCT_ACTION.UNSUSPEND)}
            onClick={() => {
              setAction(SIM_PRODUCT_ACTION.UNSUSPEND);
            }}
          >
            {t('products.sim.actions.unsuspend')}
          </MenuItem>
        </SimInventoryCan>

        <SimInventoryCan I={Actions.make} a={Subjects.inLifeTermination}>
          <MenuItem
            disabled={!inLifeActions.has(SIM_PRODUCT_ACTION.TERMINATE)}
            onClick={() => {
              setAction(SIM_PRODUCT_ACTION.TERMINATE);
            }}
          >
            {t('products.sim.actions.terminate')}
          </MenuItem>
        </SimInventoryCan>

        <SimInventoryCan I={Actions.make} a={Subjects.inLifeCancelLocation}>
          <MenuItem
            disabled={!inLifeActions.has(SIM_PRODUCT_ACTION.CANCEL_LOCATION)}
            onClick={() => {
              setAction(SIM_PRODUCT_ACTION.CANCEL_LOCATION);
            }}
          >
            {t('products.sim.actions.sendCancelLocation')}
          </MenuItem>
        </SimInventoryCan>
      </Menu>
      {action === null ? null : (
        <ActionConfirmDialog
          isPending={isPending}
          notSupportedCardIccids={inLifeActionsNotSupportedSummary[action]}
          action={action}
          onCancel={() => {
            setAction(null);
          }}
          onConfirm={() => {
            makeAction(action, { euiccIds: inLifeActionsSupportedSummary[action] });
          }}
        />
      )}
      <Portal>
        <Stack>
          <AutoHideSnackbar
            severity="info"
            open={actionSucceeded}
            onClose={() => {
              setActionSucceeded(false);
            }}
          >
            <AlertTitle>
              {t('products.sim.actions.snackbars.success.title', {
                action: actionNameRef.current,
              })}
              !
            </AlertTitle>
            <Typography variant="text1" color="text.white">
              {t('products.sim.actions.snackbars.success.description')}
            </Typography>
          </AutoHideSnackbar>
          <AutoHideSnackbar
            open={actionFailed}
            severity="error"
            onClose={() => setActionFailed(false)}
          >
            <AlertTitle>
              {t('products.sim.actions.snackbars.fail.title', {
                action: actionNameRef.current,
              })}
              !
            </AlertTitle>
            <Typography variant="text1" color="text.white">
              {t('products.sim.actions.snackbars.fail.description')}
            </Typography>
          </AutoHideSnackbar>
        </Stack>
      </Portal>
    </>
  );
};
